import logoutObserver from '@/observers/LogoutObserver';
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getNextApiUrl } from '@/utils/next-api';
import { getAppVersion } from '@/utils/application';
import { getUTMStringFromLocalStorage } from '@/containers/AnalyticsContainer/UTMContainer/UTMContainer.utils';
import { TApiError } from './ReactQuery';

const isSSR: boolean = typeof window === 'undefined';
const lastVersion = getAppVersion();

const axiosInstance: AxiosInstance = axios.create({
  timeout: 150000,
  withCredentials: false,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const utmString = isSSR ? null : getUTMStringFromLocalStorage();
    return Object.assign(config, {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Appname-Versionname': lastVersion,
        Pragma: 'no-cache',
        Expires: '0',
        ...(utmString ? { 'utm-params': utmString } : {}),
        ...(config?.headers || {}),
      },
    });
  },
  (error) => Promise.reject(error)
);

export const request = async ({
  resolveInterceptor = (response) => response?.data?.data,
  rejectInterceptor = (error) => Promise.reject(error),
  ...config
}: AxiosRequestConfig & { shouldReturnDetailedError?: boolean } & {
  resolveInterceptor?: (__response: AxiosResponse) => any;
  rejectInterceptor?: (__error: TApiError) => any;
}) => {
  try {
    const response = await axiosInstance.request(config);
    return resolveInterceptor(response);
  } catch (error: any) {
    let message: string = !error?.response
      ? 'ارتباط با سرور برقرار نشد.'
      : error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        error?.response?.statusText;

    let code: string | number =
      `${error?.response?.data?.error?.code || ''}` ||
      error?.response?.status ||
      error?.code;

    if (error?.response?.status?.toString() === '401') {
      code = error?.response?.status;
      message = message || 'ابتدا باید وارد سایت شوید.';

      // Logout user if the access token is expired and Token exists in cookies
      const hasAccessToken = Boolean(
        error?.response?.config?.headers?.Authorization
      );

      if (hasAccessToken) {
        logoutObserver.notify(null);
        if (typeof window !== 'undefined') {
          window.location.href = getNextApiUrl('auth/logout');
        }
      }
    }

    const detailedError = {
      serviceName: error?.request?.responseURL,
      status: error?.status,
    };

    return rejectInterceptor({
      code,
      message: message || 'خطای ناشناخته رخ داده است.',
      displayMode: error?.response?.data?.error?.displayMode,
      ...(config?.shouldReturnDetailedError ? detailedError : {}),
    });
  }
};

export default request;
